import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Paperclip } from 'lucide-react';
import { RENEWAL_REQUEST_STATUS, formatMoney, type RenewalRequestDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { dateOnly, dateTime, toIsoDate } from '@/lib/utils';
import { statusLabel } from '@/components/ui/badge';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Alert, Badge, Button, DatePicker, Field, Select } from '@/components/ui/primitives';
import { OwnerConfirmDialog } from './OwnerConfirm';
import { OWNER_DESK, openDeskAttachment } from '../desk/desk-api';

/** Sensible default when granting: a year from today. */
function oneYearFromNow(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return toIsoDate(date);
}

/**
 * Renewal requests waiting on the owner.
 *
 * Two things happen here that happen nowhere else: somebody looks at the
 * payment proof and decides the money arrived, and somebody picks the date
 * access runs to. The date is a judgement — a part payment, a month while a
 * cheque clears — so it is asked for rather than derived from the plan cycle.
 */
export function OwnerRenewalsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [approving, setApproving] = useState<RenewalRequestDto | null>(null);
  const [rejecting, setRejecting] = useState<RenewalRequestDto | null>(null);
  const [until, setUntil] = useState(oneYearFromNow());

  const renewals = useQuery({
    queryKey: ['owner-renewals', status],
    queryFn: () => get<RenewalRequestDto[]>(`/platform/renewals${qs({ status: status || undefined, limit: 100 })}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['owner-renewals'] });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  return (
    <>
      <PageHeader
        title="Renewals"
        description="Payment proofs sent in by account managers"
      />

      <Alert className="mb-4">
        Approving extends the facility&rsquo;s access to the date you pick and lifts read-only mode.
        Check the payment actually landed first — nothing here verifies that for you.
      </Alert>

      <SearchFilterBar
        filters={
          <FilterField label="Status">
            <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              {RENEWAL_REQUEST_STATUS.map((value) => (
                <option key={value} value={value}>
                  {statusLabel(value, 'renewal_request_status')}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={status !== 'pending' ? () => setStatus('pending') : undefined}
      />

      <DataTable
        rows={renewals.data}
        loading={renewals.isLoading} refreshing={renewals.isFetching}
        error={renewals.error}
        rowKey={(row) => row.id}
        empty="Nothing waiting."
        columns={[
          {
            header: 'Facility',
            sort: (row) => row.tenantName,
            cell: (row) => (
              <div>
                <div className="font-medium">{row.tenantName}</div>
                <div className="text-xs text-muted-foreground">
                  {row.requestedByName} · {dateTime(row.createdAt)}
                </div>
              </div>
            ),
          },
          { header: 'Amount', align: 'right', sort: (row) => row.amountMinor, cell: (row) => formatMoney(row.amountMinor, row.currency) },
          { header: 'Reference', sort: (row) => row.reference, cell: (row) => row.reference ?? '—' },
          {
            header: 'Proof',
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                {row.attachments.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    className="flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs hover:bg-accent focus-ring"
                    onClick={() => void openDeskAttachment(OWNER_DESK, file.id)}
                  >
                    <Paperclip className="h-3 w-3" /> {file.fileName.slice(0, 18)}
                  </button>
                ))}
              </div>
            ),
          },
          {
            header: 'Status',
            sort: (row) => row.status,
            cell: (row) => (
              <div>
                <Badge
                  variant={
                    row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'destructive' : 'warning'
                  }
                >
                  {statusLabel(row.status, 'renewal_request_status')}
                </Badge>
                {row.approvedUntil && (
                  <div className="mt-0.5 text-xs text-muted-foreground">Until {dateOnly(row.approvedUntil)}</div>
                )}
              </div>
            ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) =>
              row.status === 'pending' ? (
                <div className="flex justify-end gap-2">
                  <Button size="sm" onClick={() => { setUntil(oneYearFromNow()); setApproving(row); }}>
                    Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => setRejecting(row)}>
                    Reject
                  </Button>
                </div>
              ) : null,
          },
        ]}
      />

      {approving && (
        <OwnerConfirmDialog
          open
          onClose={() => setApproving(null)}
          title={`Approve ${approving.tenantName}`}
          description={`${formatMoney(approving.amountMinor, approving.currency)} — check the proof before you confirm.`}
          confirmLabel="Approve renewal"
          extraValid={Boolean(until)}
          successMessage="Renewal approved"
          action={({ totpCode, reason }) =>
            post(`/platform/renewals/${approving.id}/approve`, {
              totpCode,
              approvedUntil: new Date(`${until}T23:59:59`).toISOString(),
              note: reason,
            })
          }
          onDone={() => {
            setApproving(null);
            refresh();
          }}
        >
          <Field label="Access runs to" required hint="The last day this facility can write">
            <DatePicker value={until} onChange={setUntil} />
          </Field>
        </OwnerConfirmDialog>
      )}

      {rejecting && (
        <OwnerConfirmDialog
          open
          onClose={() => setRejecting(null)}
          title={`Reject ${rejecting.tenantName}`}
          destructive
          confirmLabel="Reject request"
          warning="The account manager sees your reason and can send a corrected request."
          successMessage="Request rejected"
          action={({ totpCode, reason }) =>
            post(`/platform/renewals/${rejecting.id}/reject`, { totpCode, note: reason })
          }
          onDone={() => {
            setRejecting(null);
            refresh();
          }}
        />
      )}
    </>
  );
}
